// Aprendizado da IA — gráfico semanal, funil (só com vendas), onde os clientes param e motivos.
// Gráfico: uma série só (taxa de sucesso), barras finas com ponta arredondada ancorada na base,
// grade recessiva, dica por barra e tabela equivalente para leitor de tela.
import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Tooltip } from "@material-ui/core";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import moment from "moment";

import { i18n } from "../../translate/i18n";
import { Section } from "../../components/ui";
import { getTokens } from "../../styles/tokens";
import { fmtPct, reasonText } from "./format";

const CHART_H = 180;

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    hint: { display: "block", marginTop: 2, fontSize: 13, fontWeight: 400, color: t.textSecondary },
    chart: { display: "flex", gap: 8, paddingTop: 6 },
    yAxis: {
      position: "relative",
      width: 34,
      flex: "none",
      height: CHART_H,
      fontSize: 11,
      color: t.textMuted,
      fontVariantNumeric: "tabular-nums",
    },
    yTick: { position: "absolute", right: 0, transform: "translateY(-50%)" },
    plotWrap: { flex: 1, minWidth: 0 },
    plot: {
      position: "relative",
      height: CHART_H,
      display: "flex",
      alignItems: "flex-end",
      gap: 2,
      borderBottom: `1px solid ${t.lineStrong}`,
    },
    grid: { position: "absolute", left: 0, right: 0, borderTop: `1px dashed ${t.line}`, pointerEvents: "none" },
    col: {
      flex: 1,
      minWidth: 0,
      height: "100%",
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "center",
      cursor: "default",
      position: "relative",
      zIndex: 1,
      borderRadius: 4,
      "&:hover": { backgroundColor: t.rowHover },
    },
    bar: {
      width: "56%",
      maxWidth: 36,
      minHeight: 2,
      backgroundColor: t.brand,
      borderRadius: "4px 4px 0 0",
    },
    xAxis: { display: "flex", gap: 2, marginTop: 6 },
    xTick: {
      flex: 1,
      minWidth: 0,
      textAlign: "center",
      fontSize: 11,
      color: t.textMuted,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    srOnly: {
      position: "absolute",
      width: 1,
      height: 1,
      padding: 0,
      margin: -1,
      overflow: "hidden",
      clip: "rect(0,0,0,0)",
      border: 0,
    },
    rows: { display: "flex", flexDirection: "column", gap: 10 },
    row: { display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: "4px 12px", alignItems: "center" },
    rowLabel: { fontSize: 13, color: t.text, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    rowValue: { fontSize: 13, fontWeight: 600, color: t.text, fontVariantNumeric: "tabular-nums" },
    track: { gridColumn: "1 / -1", height: 6, borderRadius: 3, backgroundColor: t.muted, overflow: "hidden" },
    fill: { height: "100%", borderRadius: 3, backgroundColor: t.brand },
    empty: { fontSize: 13, color: t.textMuted, padding: "8px 0" },
  };
});

const sectionTitle = (title, hint, classes) => (
  <>
    {title}
    {hint && <span className={classes.hint}>{hint}</span>}
  </>
);

const weekLabel = (iso, fmt = "DD/MM") => (iso ? moment(iso, "YYYY-MM-DD").format(fmt) : "—");

const WeeklyChart = ({ weekly }) => {
  const classes = useStyles();
  const ticks = [1, 0.5, 0];
  return (
    <>
      <div className={classes.chart} aria-hidden="true">
        <div className={classes.yAxis}>
          {ticks.map((v) => (
            <span key={v} className={classes.yTick} style={{ top: `${(1 - v) * 100}%` }}>
              {fmtPct(v)}
            </span>
          ))}
        </div>
        <div className={classes.plotWrap}>
          <div className={classes.plot}>
            {ticks.slice(0, 2).map((v) => (
              <div key={v} className={classes.grid} style={{ top: `${(1 - v) * 100}%` }} />
            ))}
            {weekly.map((w) => {
              const tip = w.total
                ? i18n.t("aiLearning.chart.tooltip", {
                    week: weekLabel(w.week, "DD/MM/YYYY"),
                    success: w.success,
                    total: w.total,
                    rate: fmtPct(w.rate),
                  })
                : `${weekLabel(w.week, "DD/MM/YYYY")}: ${i18n.t("aiLearning.chart.empty")}`;
              return (
                <Tooltip key={w.week} title={tip} placement="top" arrow>
                  <div className={classes.col} data-testid="week-col">
                    {w.total > 0 && <div className={classes.bar} style={{ height: `${Math.max(0, Math.min(1, w.rate)) * 100}%` }} />}
                  </div>
                </Tooltip>
              );
            })}
          </div>
          <div className={classes.xAxis}>
            {weekly.map((w) => (
              <span key={w.week} className={classes.xTick}>
                {weekLabel(w.week)}
              </span>
            ))}
          </div>
        </div>
      </div>
      <table className={classes.srOnly}>
        <caption>{i18n.t("aiLearning.chart.title")}</caption>
        <thead>
          <tr>
            <th>{i18n.t("aiLearning.chart.week")}</th>
            <th>{i18n.t("aiLearning.chart.success")}</th>
            <th>{i18n.t("aiLearning.chart.total")}</th>
          </tr>
        </thead>
        <tbody>
          {weekly.map((w) => (
            <tr key={w.week}>
              <td>{weekLabel(w.week, "DD/MM/YYYY")}</td>
              <td>{w.success}</td>
              <td>{w.total}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

const BarList = ({ items, emptyText }) => {
  const classes = useStyles();
  if (!items.length) return <div className={classes.empty}>{emptyText}</div>;
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <div className={classes.rows}>
      {items.map((it) => (
        <div key={it.key} className={classes.row}>
          <span className={classes.rowLabel} title={it.label}>
            {it.label}
          </span>
          <span className={classes.rowValue}>{it.count}</span>
          <div className={classes.track}>
            <div className={classes.fill} style={{ width: `${(it.count / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const FUNNEL_STEPS = ["chegou", "cotou", "proposta", "fechou"];

const LearningReport = ({ report }) => {
  const classes = useStyles();
  const weekly = Array.isArray(report?.weekly) ? report.weekly : [];
  const funnel = report?.funnel || {};
  const hasSales = FUNNEL_STEPS.some((k) => Number(funnel[k]) > 0);
  const stops = (Array.isArray(report?.stopPoints) ? report.stopPoints : []).map((s, i) => ({
    key: `${i}-${s.stopPoint}`,
    label: reasonText(s.stopPoint),
    count: Number(s.count) || 0,
  }));
  const reasons = (Array.isArray(report?.reasons) ? report.reasons : []).map((r, i) => ({
    key: `${i}-${r.reason}`,
    label: reasonText(r.reason),
    count: Number(r.count) || 0,
  }));

  return (
    <>
      <Section title={sectionTitle(i18n.t("aiLearning.chart.title"), i18n.t("aiLearning.chart.hint"), classes)}>
        <WeeklyChart weekly={weekly} />
      </Section>

      {hasSales && (
        <Section title={i18n.t("aiLearning.funnel.title")}>
          <BarList
            items={FUNNEL_STEPS.map((k) => ({ key: k, label: i18n.t(`aiLearning.funnel.${k}`), count: Number(funnel[k]) || 0 }))}
            emptyText=""
          />
        </Section>
      )}

      <Grid2 container spacing={2}>
        <Grid2 xs={12} md={6}>
          <Section title={sectionTitle(i18n.t("aiLearning.stops.title"), i18n.t("aiLearning.stops.hint"), classes)}>
            <BarList items={stops} emptyText={i18n.t("aiLearning.stops.empty")} />
          </Section>
        </Grid2>
        <Grid2 xs={12} md={6}>
          <Section title={i18n.t("aiLearning.reasons.title")}>
            <BarList items={reasons} emptyText={i18n.t("aiLearning.reasons.empty")} />
          </Section>
        </Grid2>
      </Grid2>
    </>
  );
};

export default LearningReport;
